import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowDown, ArrowUp, Scales, ShoppingCart, MagnifyingGlass, X, Plus } from "@phosphor-icons/react";

const TYPE_META = {
  entry: { label: "Entrada", color: "#00873D", Icon: ArrowDown },
  exit: { label: "Salida", color: "#E0000F", Icon: ArrowUp },
  adjustment: { label: "Ajuste", color: "#FFC000", Icon: Scales },
  sale: { label: "Venta", color: "#002FA7", Icon: ShoppingCart },
  creation: { label: "Alta", color: "#7c3aed", Icon: Plus },
};

export default function Movements() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    // Debounce search: server-side text filter across product name / user / note.
    const t = setTimeout(() => {
      const params = { type, limit: 500 };
      if (q.trim()) params.q = q.trim();
      api.get("/movements", { params }).then((r) => setItems(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [type, q]);

  const fmt = (n, cur) => n != null ? `${cur === "USD" ? "$" : "MX$"}${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header>
        <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">REGISTRO</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Movimientos</h1>
        <p className="text-zinc-500 mt-1 text-sm">{items.length} eventos {q ? "coinciden con la búsqueda" : "registrados"}</p>
      </header>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por producto, usuario o nota…"
            className="pl-8 pr-8"
            data-testid="mov-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              data-testid="mov-search-clear"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-56" data-testid="mov-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="entry">Entradas</SelectItem>
            <SelectItem value="exit">Salidas</SelectItem>
            <SelectItem value="adjustment">Ajustes</SelectItem>
            <SelectItem value="sale">Ventas</SelectItem>
            <SelectItem value="creation">Altas (creación)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="movements-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Producto</th>
              <th className="text-right px-4 py-3">Cantidad</th>
              <th className="text-right px-4 py-3">Stock después</th>
              <th className="text-right px-4 py-3">Precio venta</th>
              <th className="text-left px-4 py-3">Tier</th>
              <th className="text-left px-4 py-3">Usuario</th>
              <th className="text-left px-4 py-3">Nota</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-zinc-400 dotted-bg">
                {q ? `Sin resultados para "${q}"` : "Sin movimientos"}
              </td></tr>
            )}
            {items.map((m) => {
              const meta = TYPE_META[m.type] || { label: m.type, color: "#52525b", Icon: Scales };
              const Icon = meta.Icon;
              const qtySign = m.type === "entry" || m.type === "creation" ? "+" : m.type === "adjustment" ? "" : "−";
              return (
                <tr key={m.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 numeric text-zinc-600 text-xs">{new Date(m.created_at).toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm" style={{ color: meta.color, background: meta.color + "15" }}>
                      <Icon size={12} weight="bold" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{m.product_name}</td>
                  <td className="px-4 py-3 text-right numeric font-semibold" style={{ color: meta.color }}>
                    {qtySign}{Math.abs(m.quantity)}
                  </td>
                  <td className="px-4 py-3 text-right numeric">{m.stock_after}</td>
                  <td className="px-4 py-3 text-right numeric">{fmt(m.sale_price, m.sale_currency)}</td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{m.price_tier || "—"}</td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{m.user_email}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs max-w-[200px] truncate">{m.note || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
